/**
 * Component folder view (Sprint 4.3.4, PROD-653).
 *
 * Lists every component instance inside a single care-flow folder.
 * Surfaces id + label + version + lastModified plus a "used by N
 * pathways" count. Clicking a row opens the `<ComponentJsonEditor>`
 * detail view for that component instance.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: rendered as a `<table>` with proper `<th>` headers; rows
 * carry an inline action button so the keyboard user gets a focusable
 * target with the correct semantics.
 */
import {
  CARE_FLOW_FOLDER_LABELS,
  isSharedFolder,
  type CareFlowComponentJson,
  type CareFlowFolder,
} from '../../services/care-flow-components/types';

export interface ComponentFolderViewProps {
  folder: CareFlowFolder;
  components: ReadonlyArray<CareFlowComponentJson>;
  selectedComponentId: string | null;
  onSelectComponent: (componentId: string) => void;
  className?: string;
}

export function ComponentFolderView({
  folder,
  components,
  selectedComponentId,
  onSelectComponent,
  className,
}: ComponentFolderViewProps) {
  const shared = isSharedFolder(folder);
  return (
    <section
      aria-labelledby={`care-flow-folder-view-title-${folder}`}
      data-testid="care-flow-folder-view"
      data-folder={folder}
      className={[
        'flex w-full flex-col gap-gutter-md p-gutter-lg',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex items-center justify-between">
        <div>
          <h3
            id={`care-flow-folder-view-title-${folder}`}
            className="text-signal-lg font-semibold text-text-primary"
          >
            {CARE_FLOW_FOLDER_LABELS[folder]}
          </h3>
          <p className="mt-1 text-signal-xs text-text-tertiary">
            {shared
              ? 'Shared component. Edits affect every pathway that cites this instance.'
              : 'Non-shared component. Lifecycle is bound to its pathway.'}
          </p>
        </div>
        <span
          data-testid={`care-flow-folder-view-count-${folder}`}
          className="rounded-signal-sm bg-surface-muted px-gutter-sm py-1 text-signal-xs font-semibold text-text-secondary"
        >
          {components.length} {components.length === 1 ? 'item' : 'items'}
        </span>
      </header>
      {components.length === 0 ? (
        <p
          data-testid={`care-flow-folder-view-empty-${folder}`}
          className="rounded-signal-md border border-dashed border-surface-border bg-surface-base p-gutter-lg text-center text-signal-sm text-text-tertiary"
        >
          No {CARE_FLOW_FOLDER_LABELS[folder].toLowerCase()} yet. Add one
          from the JSON editor.
        </p>
      ) : (
        <table
          data-testid={`care-flow-folder-view-table-${folder}`}
          className="w-full border-collapse text-left text-signal-sm"
        >
          <thead className="bg-surface-muted text-signal-xs uppercase tracking-wide text-text-tertiary">
            <tr>
              <th scope="col" className="px-gutter-md py-gutter-sm">
                ID
              </th>
              <th scope="col" className="px-gutter-md py-gutter-sm">
                Label
              </th>
              <th scope="col" className="px-gutter-md py-gutter-sm">
                Version
              </th>
              <th scope="col" className="px-gutter-md py-gutter-sm">
                Last modified
              </th>
              <th scope="col" className="px-gutter-md py-gutter-sm">
                Used by
              </th>
            </tr>
          </thead>
          <tbody>
            {components.map((component) => {
              const isSelected = component.id === selectedComponentId;
              const usedBy = component.usedByPathwayIds?.length ?? 0;
              return (
                <tr
                  key={component.id}
                  data-testid={`care-flow-folder-view-row-${component.id}`}
                  data-component-id={component.id}
                  data-row-selected={isSelected ? 'true' : 'false'}
                  className={
                    isSelected
                      ? 'border-b border-signal-primary-500 bg-surface-muted'
                      : 'border-b border-surface-border'
                  }
                >
                  <td className="px-gutter-md py-gutter-sm align-top font-mono text-signal-xs text-text-secondary">
                    <button
                      type="button"
                      data-testid={`care-flow-folder-view-open-${component.id}`}
                      onClick={() => onSelectComponent(component.id)}
                      className="text-signal-primary-500 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
                    >
                      {component.id}
                    </button>
                  </td>
                  <td className="px-gutter-md py-gutter-sm align-top text-text-primary">
                    {component.label}
                  </td>
                  <td className="px-gutter-md py-gutter-sm align-top text-text-secondary">
                    {component.version}
                  </td>
                  <td className="px-gutter-md py-gutter-sm align-top text-text-secondary">
                    {component.lastModified}
                  </td>
                  <td className="px-gutter-md py-gutter-sm align-top text-text-secondary">
                    {usedBy} {usedBy === 1 ? 'pathway' : 'pathways'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
